import {
    Box,
    HStack,
    IconButton,
    Menu,
    MenuButton,
    MenuItem,
    MenuList,
    Skeleton,
    Text,
    Tooltip,
} from '@chakra-ui/react'
import React, { useMemo } from 'react'
import {
    ChatFieldsFragment,
    ChatPreviewFieldsFragment,
    namedOperations,
    useChatStatusesQuery,
    useUpdateChatMutation,
} from '~graphql/generated/graphql'
import { useToastFeedback } from '~utils/hooks/use-toast-feedback'

type ChatStatusSquareSize = '2xs' | 'xs' | 'sm' | 'md'

interface Props {
    chat: ChatPreviewFieldsFragment | ChatFieldsFragment
    size?: ChatStatusSquareSize
}

const SIZE_PX: Record<ChatStatusSquareSize, number> = {
    '2xs': 16,
    xs: 24,
    sm: 32,
    md: 48,
}

export default function ChatStatusSelect({ chat, size = 'xs' }: Props) {
    const toasts = useToastFeedback(
        'Successfully updated chat status',
        'Error updating chat status',
    )
    const { data, loading } = useChatStatusesQuery({
        variables: { clinicId: chat.clinic?.id || '' },
        skip: !chat.clinic?.id,
        fetchPolicy: 'cache-first',
    })

    const [updateChat, { loading: isUpdating }] = useUpdateChatMutation({
        refetchQueries: [
            namedOperations.Query.queryChats,
            namedOperations.Query.queryChatPreviews,
        ],
    })

    const activeStatuses = useMemo(
        () => (data?.chatStatuses ?? []).filter(cs => !cs.is_archived),
        [data?.chatStatuses],
    )

    if (!loading && activeStatuses.length === 0) return null

    const currentStatus = chat.chat_status
    const px = SIZE_PX[size]

    async function handleSelect(chatStatusId: string | null) {
        if (chatStatusId === (currentStatus?.id ?? null)) {
            return
        }

        await updateChat({
            variables: {
                id: chat.id,
                data: { chat_status: chatStatusId },
            },
            ...toasts,
        })
    }

    return (
        <Box onClick={e => e.stopPropagation()}>
            <Skeleton isLoaded={!loading} rounded="md">
                <Menu placement="bottom-start" isLazy>
                    <Tooltip
                        openDelay={300}
                        label={
                            currentStatus
                                ? `Status: ${currentStatus.name}`
                                : 'Set chat status'
                        }
                    >
                        <MenuButton
                            as={IconButton}
                            aria-label="Chat status"
                            variant="unstyled"
                            size={size}
                            minW={`${px}px`}
                            w={`${px}px`}
                            h={`${px}px`}
                            p={0}
                            isDisabled={isUpdating}
                            display="flex"
                            alignItems="center"
                            justifyContent="center"
                            icon={
                                <Box
                                    w={`${px}px`}
                                    h={`${px}px`}
                                    rounded="md"
                                    bg={currentStatus?.color || 'transparent'}
                                    borderWidth={currentStatus ? '1px' : '2px'}
                                    borderStyle="solid"
                                    borderColor={
                                        currentStatus
                                            ? 'blackAlpha.100'
                                            : 'gray.200'
                                    }
                                    display="flex"
                                    alignItems="center"
                                    justifyContent="center"
                                    transition="border-color 0.15s"
                                    _hover={{
                                        borderColor: currentStatus
                                            ? 'blackAlpha.200'
                                            : 'gray.400',
                                    }}
                                >
                                    {currentStatus && (
                                        <Text
                                            fontSize={`${Math.max(
                                                Math.round(px * 0.42),
                                                9,
                                            )}px`}
                                            fontWeight="bold"
                                            lineHeight="1"
                                            letterSpacing="-0.02em"
                                            color={getContrastingTextColor(
                                                currentStatus.color,
                                            )}
                                            userSelect="none"
                                        >
                                            {getInitials(currentStatus.name)}
                                        </Text>
                                    )}
                                </Box>
                            }
                        />
                    </Tooltip>
                    <MenuList minW="200px" py={1} maxH="320px" overflowY="auto">
                        <ChatStatusMenuItem
                            label="None"
                            isSelected={!currentStatus}
                            onSelect={() => handleSelect(null)}
                        />
                        {activeStatuses.map(status => (
                            <ChatStatusMenuItem
                                key={status.id}
                                label={status.name}
                                color={status.color || undefined}
                                isSelected={status.id === currentStatus?.id}
                                onSelect={() => handleSelect(status.id)}
                            />
                        ))}
                    </MenuList>
                </Menu>
            </Skeleton>
        </Box>
    )
}

interface ChatStatusMenuItemProps {
    label: string
    color?: string
    isSelected: boolean
    onSelect: () => void
}

function ChatStatusMenuItem({
    label,
    color,
    isSelected,
    onSelect,
}: ChatStatusMenuItemProps) {
    return (
        <MenuItem
            onClick={onSelect}
            py={1}
            borderLeftWidth={2}
            borderLeftColor={isSelected ? 'brand.500' : 'white'}
            _focus={{
                borderLeftColor: 'brand.500',
                bg: 'gray.100',
            }}
            _active={{
                borderLeftColor: 'brand.500',
                bg: 'gray.100',
            }}
            _hover={{
                borderLeftColor: 'brand.500',
                bg: 'gray.100',
            }}
        >
            <HStack spacing={3}>
                <Box
                    w={4}
                    h={4}
                    rounded="md"
                    bg={color || 'transparent'}
                    borderWidth={color ? '1px' : '2px'}
                    borderStyle="solid"
                    borderColor={color ? 'blackAlpha.100' : 'gray.300'}
                />
                <Text fontSize="sm">{label}</Text>
            </HStack>
        </MenuItem>
    )
}

function getInitials(name: string): string {
    const words = name.trim().split(/\s+/).filter(Boolean)
    if (words.length === 0) return ''
    if (words.length === 1) return words[0].slice(0, 2).toUpperCase()
    return (words[0][0] + words[1][0]).toUpperCase()
}

/**
 * Picks a readable text color (white or near-black) for a given background
 * color using a luminance approximation. Accepts hex, `rgb()`, `hsl()`, or
 * named colors — IOSColorPicker can produce any of these.
 */
function getContrastingTextColor(color: string | null | undefined): string {
    if (!color) return 'white'

    const rgb = parseColorToRgb(color)
    if (!rgb) return 'white'

    const luminance = (0.299 * rgb.r + 0.587 * rgb.g + 0.114 * rgb.b) / 255
    return luminance > 0.6 ? 'gray.800' : 'white'
}

interface RGB {
    r: number
    g: number
    b: number
}

function parseColorToRgb(color: string): RGB | null {
    const trimmed = color.trim()

    if (trimmed.startsWith('#')) {
        return parseHex(trimmed)
    }

    const rgbMatch = trimmed.match(
        /rgba?\(\s*(\d+(?:\.\d+)?)\s*,\s*(\d+(?:\.\d+)?)\s*,\s*(\d+(?:\.\d+)?)/i,
    )
    if (rgbMatch) {
        return {
            r: Math.round(parseFloat(rgbMatch[1])),
            g: Math.round(parseFloat(rgbMatch[2])),
            b: Math.round(parseFloat(rgbMatch[3])),
        }
    }

    // Fall back to letting the browser normalize anything else (hsl, named
    // colors) by writing it into a canvas fillStyle, which always returns a
    // resolved `#rrggbb`.
    if (typeof document === 'undefined') return null
    const ctx = document.createElement('canvas').getContext('2d')
    if (!ctx) return null
    try {
        ctx.fillStyle = trimmed
        const resolved = ctx.fillStyle
        if (typeof resolved === 'string' && resolved.startsWith('#')) {
            return parseHex(resolved)
        }
    } catch {
        // Ignore — fall through to null.
    }

    return null
}

function parseHex(hex: string): RGB | null {
    let clean = hex.replace('#', '')
    if (clean.length === 3) {
        clean = clean
            .split('')
            .map(c => c + c)
            .join('')
    }
    if (clean.length !== 6) return null

    const r = parseInt(clean.slice(0, 2), 16)
    const g = parseInt(clean.slice(2, 4), 16)
    const b = parseInt(clean.slice(4, 6), 16)
    if ([r, g, b].some(Number.isNaN)) return null

    return { r, g, b }
}
